import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Drawer from "@mui/material/Drawer";
import { Stack, Box, Typography, Grid, IconButton, MenuItem, FormControl, InputLabel, Select, Divider, Checkbox, FormControlLabel, RadioGroup, Radio, ListSubheader } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SearchInput from "../../components/SearchInput";
import { useForm } from "react-hook-form";
import { useCouponStore } from "../../services/coupons";
import { useCompanyStore } from "../../services/company";
import { useCorporateStore } from "../../services/corporates";
import { useSpecialistStore } from "../../services/specialist";
import { useWellnessPackageStore } from "../../services/wellnessPackages";
import { ICoupon, CreateCouponRequest } from "../../types/coupons";
import { ExternalCatalogOption } from "../../types/bookings";
import { DISCOUNT_TYPES, APPLICABLE_TO_TYPES } from "./constants";
import { showError } from "../../services/toaster";
import dayjs from "dayjs";
import CustomImage from "../../components/CustomImage";
import ImageUpload from "../../components/ImageUploader";
import { uploadFile } from "../../utils/helper";
import { MODULES } from "../../utils/constants";
import { doGET } from "../../utils/HttpUtils";
import {
    fetchExternalProviderTypes,
    fetchExternalSpecialists,
    fetchExternalLabs,
    fetchExternalPackages,
    fetchExternalTests,
} from "../../services/externalBookings";
import { UserData } from "../../types/user";

type CouponAudience = 'all' | 'corporate' | 'user' | 'user_and_corporate';
type InternalTargetMode = 'all' | 'specific';

export default function AddCouponDialog({
    isModalOpen,
    toggleModal,
    selectedId,
    hideAddButton = false
}: any) {
    const defaultData: Partial<CreateCouponRequest> = {
        code: "",
        title: "",
        description: "",
        discountType: 'percentage',
        discountValue: 0,
        minimumPurchaseAmount: 0,
        maximumDiscountAmount: 0,
        forUser: 'all',
        applicableTo: 'all',
        startDate: dayjs().format('YYYY-MM-DD'),
        endDate: dayjs().add(1, 'month').format('YYYY-MM-DD'),
        usageLimitPerUser: 1,
        totalUsageLimit: 100,
        isActive: true,
    }

    const { onCreate, detail, onUpdate, filters, setFilters } = useCouponStore();
    const { globalCompanyId } = useCompanyStore();
    const { allData: corporates, fetchAll: fetchAllCorporates } = useCorporateStore();
    const { fetchByCorporate: fetchSpecialistsByCorporate, fetchGrid: fetchSpecialistsGrid } = useSpecialistStore();
    const { fetchByCorporate: fetchPackagesByCorporate, fetchGrid: fetchPackagesGrid } = useWellnessPackageStore();
    const [couponData, setCouponData] = React.useState<Partial<CreateCouponRequest>>(defaultData)
    const [targetType, setTargetType] = React.useState<CouponAudience>('all');
    const [selectedCorporateId, setSelectedCorporateId] = React.useState<string>("");
    const [userOptions, setUserOptions] = React.useState<UserData[]>([]);
    const [userTotal, setUserTotal] = React.useState(0);
    const [userPage, setUserPage] = React.useState(1);
    const [hasMoreUsers, setHasMoreUsers] = React.useState(false);
    const [usersLoading, setUsersLoading] = React.useState(false);
    const [userSearchQuery, setUserSearchQuery] = React.useState('');
    const [applicableOptions, setApplicableOptions] = React.useState<Array<{ _id: string; label: string }>>([]);
    const [selectedApplicableId, setSelectedApplicableId] = React.useState<string>("");

    /** All = all booking types; Internal = in-app wellness/specialist; External = Labstack external specialist / diagnostics */
    const [bookingScope, setBookingScope] = React.useState<'all' | 'internal' | 'external'>('internal');
    const [internalTargetMode, setInternalTargetMode] = React.useState<InternalTargetMode>('all');
    const [externalKind, setExternalKind] = React.useState<'external_specialist' | 'external_diagnostics'>('external_specialist');
    const [extAppointmentType, setExtAppointmentType] = React.useState('ONLINE');
    const [extPincode, setExtPincode] = React.useState('');
    const [extLat, setExtLat] = React.useState('');
    const [extLng, setExtLng] = React.useState('');
    const [providerTypes, setProviderTypes] = React.useState<Array<{ providerType: string; name?: string }>>([]);
    const [selectedProviderType, setSelectedProviderType] = React.useState('');
    const [externalSpecialistsList, setExternalSpecialistsList] = React.useState<Array<{ id: string; name: string }>>([]);
    const [selectedExternalSpecialistId, setSelectedExternalSpecialistId] = React.useState('');
    /** When true, coupon applies to any external specialist booking (eligibleFor still required). */
    const [extTargetAllSpecialists, setExtTargetAllSpecialists] = React.useState(false);
    /** External diagnostics can target the whole category or a specific lab/package/test set. */
    const [extTargetAllDiagnostics, setExtTargetAllDiagnostics] = React.useState(false);
    const [externalLabsList, setExternalLabsList] = React.useState<Array<{ id: string; name: string }>>([]);
    const [selectedLabId, setSelectedLabId] = React.useState('');
    const [externalPackagesList, setExternalPackagesList] = React.useState<ExternalCatalogOption[]>([]);
    const [selectedPackageIds, setSelectedPackageIds] = React.useState<string[]>([]);
    const [packageSearchInput, setPackageSearchInput] = React.useState('');
    const [packageSearchQuery, setPackageSearchQuery] = React.useState('');
    const [externalTestsList, setExternalTestsList] = React.useState<ExternalCatalogOption[]>([]);
    const [selectedTestIds, setSelectedTestIds] = React.useState<string[]>([]);
    const [testSearchInput, setTestSearchInput] = React.useState('');
    const [testSearchQuery, setTestSearchQuery] = React.useState('');
    const [extDiagBothRequired, setExtDiagBothRequired] = React.useState(false);

    const needsCorporate = targetType === 'corporate' || targetType === 'user_and_corporate';
    const needsUsers = targetType === 'user' || targetType === 'user_and_corporate';
    const selectedUserIds = Array.isArray(couponData.userIds)
        ? couponData.userIds.filter((userId): userId is string => typeof userId === 'string' && !!userId)
        : [];
    const canSelectAllInternal = !selectedId || !couponData.eligibleFor;
    const canRequireDiagnosticBoth =
        !extTargetAllDiagnostics &&
        !!selectedLabId &&
        (selectedPackageIds.length > 0 || selectedTestIds.length > 0);
    const appliesToWalletRecharge =
        bookingScope === 'all' ||
        (bookingScope === 'internal' && couponData.applicableTo === 'all') ||
        (bookingScope === 'external' &&
            ((externalKind === 'external_specialist' && extTargetAllSpecialists) ||
                (externalKind === 'external_diagnostics' && extTargetAllDiagnostics)));
    const availableUserOptions = targetType === 'user_and_corporate'
        ? userOptions.filter((user) =>
            !!selectedCorporateId && String(user.corporateId || '') === String(selectedCorporateId)
        )
        : userOptions;
    const selectableUserOptions = availableUserOptions.filter(
        (user): user is UserData & { _id: string } => !!user._id
    );
    const normalizedUserSearch = userSearchQuery.trim().toLowerCase();
    const filteredSelectableUserOptions = normalizedUserSearch
        ? selectableUserOptions.filter((user) =>
            [user.name, user.email, user.phone]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(normalizedUserSearch))
        )
        : selectableUserOptions;
    const normalizedPackageSearch = packageSearchQuery.trim().toLowerCase();
    const filteredExternalPackages = normalizedPackageSearch
        ? externalPackagesList.filter((item) =>
            [item.name, item.id]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(normalizedPackageSearch))
        )
        : externalPackagesList;
    const normalizedTestSearch = testSearchQuery.trim().toLowerCase();
    const filteredExternalTests = normalizedTestSearch
        ? externalTestsList.filter((item) =>
            [item.name, item.id]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(normalizedTestSearch))
        )
        : externalTestsList;

    const imageFileRef = React.useRef<File | null>(null);
    const [existingImageUrl, setExistingImageUrl] = React.useState<string>("");

    const handleChange = (key: keyof CreateCouponRequest, value: any) => {
        setCouponData(prev => ({ ...prev, [key]: value }));
        if (fieldErrors[key]) setFieldErrors(prev => ({ ...prev, [key]: '' }));
    }

    const [fieldErrors, setFieldErrors] = React.useState<{ [key: string]: string }>({});

    const { handleSubmit } = useForm<CreateCouponRequest>();

    const formatExternalCatalogPrice = (item: ExternalCatalogOption) => {
        const amount = typeof item.customPrice === 'number'
            ? item.customPrice
            : typeof item.price === 'number'
                ? item.price
                : undefined;

        return typeof amount === 'number' ? `Rs. ${amount}` : null;
    };

    const handleClickOpen = () => {
        setCouponData(defaultData);
        const isCorporateScope = !!globalCompanyId && globalCompanyId !== 'general';
        setTargetType(isCorporateScope ? 'corporate' : 'all');
        setSelectedCorporateId(isCorporateScope ? globalCompanyId : "");
        setUserOptions([]);
        setSelectedApplicableId("");
        setApplicableOptions([]);
        setBookingScope('internal');
        setInternalTargetMode('all');
        setExternalKind('external_specialist');
        setExtAppointmentType('ONLINE');
        setExtPincode('');
        setExtLat('');
        setExtLng('');
        setProviderTypes([]);
        setSelectedProviderType('');
        setExternalSpecialistsList([]);
        setSelectedExternalSpecialistId('');
        setExtTargetAllSpecialists(false);
        setExtTargetAllDiagnostics(false);
        setExternalLabsList([]);
        setSelectedLabId('');
        setExternalPackagesList([]);
        setSelectedPackageIds([]);
        setPackageSearchInput('');
        setPackageSearchQuery('');
        setExternalTestsList([]);
        setSelectedTestIds([]);
        setTestSearchInput('');
        setTestSearchQuery('');
        setExtDiagBothRequired(false);
        imageFileRef.current = null;
        setExistingImageUrl("");
        toggleModal(true);
    };

    const handleClose = () => {
        toggleModal(false);
        setCouponData(defaultData);
        setTargetType('all');
        setSelectedCorporateId("");
        setUserOptions([]);
        setSelectedApplicableId("");
        setApplicableOptions([]);
        setBookingScope('internal');
        setInternalTargetMode('all');
        setExternalKind('external_specialist');
        setExtAppointmentType('ONLINE');
        setExtPincode('');
        setExtLat('');
        setExtLng('');
        setProviderTypes([]);
        setSelectedProviderType('');
        setExternalSpecialistsList([]);
        setSelectedExternalSpecialistId('');
        setExtTargetAllSpecialists(false);
        setExtTargetAllDiagnostics(false);
        setExternalLabsList([]);
        setSelectedLabId('');
        setExternalPackagesList([]);
        setSelectedPackageIds([]);
        setPackageSearchInput('');
        setPackageSearchQuery('');
        setExternalTestsList([]);
        setSelectedTestIds([]);
        setTestSearchInput('');
        setTestSearchQuery('');
        setExtDiagBothRequired(false);
        imageFileRef.current = null;
        setExistingImageUrl("");
    };

    // Filter states
    const [searchQuery, setSearchQuery] = React.useState<string>("");
    const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const applyFilters = (query: string) => {
        const newFilters: any = { ...filters };

        if (query.trim()) {
            const searchTerm = query.trim().toLowerCase();

            // Check if search matches a discount type
            const matchedDiscount = DISCOUNT_TYPES.find(
                t => t.label.toLowerCase().includes(searchTerm) || t.value.toLowerCase().includes(searchTerm)
            );

            // Check if search matches an applicableTo type
            const matchedApplicable = APPLICABLE_TO_TYPES.find(
                t => t.label.toLowerCase().includes(searchTerm) || t.value.toLowerCase().includes(searchTerm)
            );

            if (matchedDiscount) {
                newFilters.discountType = matchedDiscount.value;
                delete newFilters.applicableTo;
                delete newFilters.code;
            } else if (matchedApplicable) {
                newFilters.applicableTo = matchedApplicable.value;
                delete newFilters.discountType;
                delete newFilters.code;
            } else {
                // Default: search by coupon code
                newFilters.code = query.trim();
                delete newFilters.discountType;
                delete newFilters.applicableTo;
            }
        } else {
            delete newFilters.code;
            delete newFilters.discountType;
            delete newFilters.applicableTo;
            delete newFilters.search;
        }

        setFilters(newFilters);
    };

    const handleSearchChange = (e: any) => {
        const value = e.target.value;
        setSearchQuery(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            applyFilters(value);
        }, 300);
    };

    const validateForm = (): boolean => {
        const errs: { [key: string]: string } = {};

        if (!couponData.code || couponData.code.trim().length === 0) {
            errs.code = 'Coupon code is required';
        } else if (couponData.code.trim().length < 3) {
            errs.code = 'Coupon code must be at least 3 characters';
        } else if (!/^[A-Z0-9]+$/.test(couponData.code.trim())) {
            errs.code = 'Coupon code must contain only uppercase letters and numbers';
        }


        if (!couponData.discountValue || Number(couponData.discountValue) <= 0) {
            errs.discountValue = 'Discount value must be greater than 0';
        } else if (couponData.discountType === 'percentage' && Number(couponData.discountValue) > 100) {
            errs.discountValue = 'Percentage discount cannot exceed 100%';
        }

        if (couponData.discountType === 'percentage' && couponData.maximumDiscountAmount !== undefined && String(couponData.maximumDiscountAmount) !== '' && Number(couponData.maximumDiscountAmount) < 0) {
            errs.maximumDiscountAmount = 'Max discount amount cannot be negative';
        }

        if (couponData.minimumPurchaseAmount !== undefined && String(couponData.minimumPurchaseAmount) !== '' && Number(couponData.minimumPurchaseAmount) < 0) {
            errs.minimumPurchaseAmount = 'Min purchase amount cannot be negative';
        }

        if (!couponData.startDate) {
            errs.startDate = 'Start date is required';
        }

        if (!couponData.endDate) {
            errs.endDate = 'End date is required';
        } else if (couponData.startDate && couponData.endDate && couponData.endDate < couponData.startDate) {
            errs.endDate = 'End date must be after start date';
        }

        if (couponData.usageLimitPerUser !== undefined && String(couponData.usageLimitPerUser) !== '' && Number(couponData.usageLimitPerUser) < 1) {
            errs.usageLimitPerUser = 'Limit per user must be at least 1';
        }

        if (couponData.totalUsageLimit !== undefined && String(couponData.totalUsageLimit) !== '' && Number(couponData.totalUsageLimit) < 1) {
            errs.totalUsageLimit = 'Total usage limit must be at least 1';
        }

        if (needsCorporate && !selectedCorporateId) {
            errs.corporateIds = 'Please select a corporate';
        }

        if (needsUsers && selectedUserIds.length === 0) {
            errs.userIds = 'Please select at least one user';
        }

        if (bookingScope === 'internal') {
            if (couponData.applicableTo === 'all' && !canSelectAllInternal) {
                errs.applicableTo = 'The backend cannot remove eligibleFor while updating an existing coupon';
            }
            if (
                (couponData.applicableTo === 'wellnessPackage' || couponData.applicableTo === 'consultation') &&
                internalTargetMode === 'specific' &&
                !selectedApplicableId
            ) {
                errs.applicableIds = `Please select a ${couponData.applicableTo === 'wellnessPackage' ? 'wellness package' : 'specialist'}`;
            }
        } else if (bookingScope === 'external') {
            if (externalKind === 'external_specialist') {
                if (!extTargetAllSpecialists) {
                    if (!selectedProviderType) {
                        errs.externalProvider = 'Select a provider type';
                    }
                    if (!selectedExternalSpecialistId) {
                        errs.externalSpecialist = 'Select an external specialist or choose “All external specialists”';
                    }
                }
            } else if (!extTargetAllDiagnostics) {
                const hasLab = !!selectedLabId;
                const hasPkg = selectedPackageIds.length > 0;
                const hasTests = selectedTestIds.length > 0;
                if (!hasLab && !hasPkg && !hasTests) {
                    errs.externalDiagnostics = 'Select at least one lab, package, or test';
                }
            }
        }

        setFieldErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const onSubmit = async () => {
        if (!validateForm()) {
            showError('Please fix the highlighted errors');
            return;
        }

        let imageUrl = existingImageUrl || "";
        if (imageFileRef.current instanceof File) {
            const uploadRes = await uploadFile({ module: MODULES.COUPON }, [imageFileRef.current]);
            if (uploadRes.error) {
                showError(uploadRes.message || 'Failed to upload image');
                return;
            }
            const uploadedFiles = uploadRes.data?.data?.files;
            if (uploadedFiles?.length && uploadedFiles[0]?.url) {
                imageUrl = uploadedFiles[0].url;
            }
        }

        // Format dates (YYYY-MM-DD to YYYYMMDD)
        const formattedStartDate = couponData.startDate ? couponData.startDate.replace(/-/g, '') : '';
        const formattedEndDate = couponData.endDate ? couponData.endDate.replace(/-/g, '') : '';

        const payload: any = {
            ...couponData,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            imageUrl: imageUrl,
            // Ensure numerical values are numbers
            discountValue: Number(couponData.discountValue),
        };
        delete payload.currentUsageCount;

        if (needsCorporate && selectedCorporateId) {
            payload.corporateIds = [selectedCorporateId];
        } else {
            payload.corporateIds = [];
        }

        if (needsUsers) {
            payload.userIds = selectedUserIds;
        } else {
            payload.userIds = [];
        }

        if (bookingScope === 'external') {
            payload.applicableIds = [];
            payload.applicablePackageIds = [];
            payload.applicableTestIds = [];
            payload.isBothRequired = false;

            if (externalKind === 'external_specialist') {
                payload.eligibleFor = 'external_specialist';
                if (extTargetAllSpecialists) {
                    payload.applicableTo = 'all';
                } else {
                    payload.applicableTo = 'specific';
                    payload.applicableIds = [selectedExternalSpecialistId];
                }
            } else {
                payload.eligibleFor = 'external_diagnostics';
                if (extTargetAllDiagnostics) {
                    payload.applicableTo = 'all';
                } else {
                    payload.applicableTo = 'specific';
                    payload.applicableIds = selectedLabId ? [selectedLabId] : [];
                    payload.applicablePackageIds = selectedPackageIds;
                    payload.applicableTestIds = selectedTestIds;
                    payload.isBothRequired = extDiagBothRequired && canRequireDiagnosticBoth;
                }
            }
        } else if (bookingScope === 'internal') {
            payload.applicableIds = [];
            payload.applicablePackageIds = [];
            payload.applicableTestIds = [];
            payload.isBothRequired = false;

            if (
                (couponData.applicableTo === 'wellnessPackage' || couponData.applicableTo === 'consultation') &&
                internalTargetMode === 'specific' &&
                selectedApplicableId
            ) {
                payload.applicableTo = 'specific';
                payload.applicableIds = [selectedApplicableId];
                payload.eligibleFor = couponData.applicableTo === 'wellnessPackage' ? 'package' : 'specialist';
            } else {
                if (couponData.applicableTo === 'wellnessPackage') {
                    payload.eligibleFor = 'package';
                    payload.applicableTo = 'wellnessPackage';
                } else if (couponData.applicableTo === 'consultation') {
                    payload.eligibleFor = 'specialist';
                    payload.applicableTo = 'consultation';
                } else {
                    delete payload.eligibleFor;
                    payload.applicableTo = 'all';
                }
            }
        } else {
            payload.applicableIds = [];
            payload.applicablePackageIds = [];
            payload.applicableTestIds = [];
            payload.isBothRequired = false;
            payload.eligibleFor = 'all';
            payload.applicableTo = 'all';
        }

        if (couponData.minimumPurchaseAmount !== undefined && String(couponData.minimumPurchaseAmount) !== '') {
            payload.minimumPurchaseAmount = Number(couponData.minimumPurchaseAmount);
        } else {
            delete payload.minimumPurchaseAmount;
        }

        if (couponData.maximumDiscountAmount !== undefined && String(couponData.maximumDiscountAmount) !== '') {
            payload.maximumDiscountAmount = Number(couponData.maximumDiscountAmount);
        } else {
            delete payload.maximumDiscountAmount;
        }

        if (couponData.usageLimitPerUser !== undefined && String(couponData.usageLimitPerUser) !== '') {
            payload.usageLimitPerUser = Number(couponData.usageLimitPerUser);
        } else {
            delete payload.usageLimitPerUser;
        }

        if (couponData.totalUsageLimit !== undefined && String(couponData.totalUsageLimit) !== '') {
            payload.totalUsageLimit = Number(couponData.totalUsageLimit);
        } else {
            delete payload.totalUsageLimit;
        }

        // Do not send empty optional scalar fields. Empty targeting arrays are kept
        // intentionally so an update can remove restrictions saved previously.
        Object.keys(payload).forEach((key) => {
            const value = payload[key];
            if (
                value === undefined ||
                value === null ||
                (typeof value === 'string' && value.trim() === '') ||
                (typeof value === 'number' && Number.isNaN(value))
            ) {
                delete payload[key];
            }
        });

        if (selectedId) {
            onUpdate({ _id: selectedId, ...payload } as ICoupon);
        } else {
            onCreate(payload as CreateCouponRequest);
        }
        handleClose();
    };


    const fetchDetail = async (id: string) => {
        try {
            const response = await detail(id);
            const coupon = response;
            if (coupon) {
                // Format dates for input (YYYYMMDD to YYYY-MM-DD)
                const start = coupon.startDate;
                const end = coupon.endDate;

                const formattedStart = start && start.length === 8
                    ? `${start.substring(0, 4)}-${start.substring(4, 6)}-${start.substring(6, 8)}`
                    : start;

                const formattedEnd = end && end.length === 8
                    ? `${end.substring(0, 4)}-${end.substring(4, 6)}-${end.substring(6, 8)}`
                    : end;


                const isAllEligible = coupon.eligibleFor === 'all';
                const isExternal =
                    coupon.eligibleFor === 'external_specialist' ||
                    coupon.eligibleFor === 'external_diagnostics';

                if (isAllEligible) {
                    setBookingScope('all');
                    setInternalTargetMode('all');
                    setCouponData({
                        ...coupon as unknown as CreateCouponRequest,
                        applicableTo: 'all',
                        startDate: formattedStart,
                        endDate: formattedEnd,
                    });
                } else if (isExternal) {
                    setBookingScope('external');
                    if (coupon.eligibleFor === 'external_diagnostics') {
                        setExternalKind('external_diagnostics');
                        const targetsAllDiagnostics = coupon.applicableTo === 'all';
                        setExtTargetAllDiagnostics(targetsAllDiagnostics);
                        setSelectedLabId(targetsAllDiagnostics ? '' : coupon.applicableIds?.[0] || '');
                        setSelectedPackageIds(targetsAllDiagnostics ? [] : coupon.applicablePackageIds || []);
                        setSelectedTestIds(targetsAllDiagnostics ? [] : coupon.applicableTestIds || []);
                        setExtDiagBothRequired(!targetsAllDiagnostics && !!coupon.isBothRequired);
                    } else {
                        setExternalKind('external_specialist');
                        if (coupon.applicableTo === 'all') {
                            setExtTargetAllSpecialists(true);
                            setSelectedExternalSpecialistId('');
                        } else {
                            setExtTargetAllSpecialists(false);
                            setSelectedExternalSpecialistId(coupon.applicableIds?.[0] || '');
                        }
                    }
                    const mappedApplicableTo = coupon.applicableTo;
                    setCouponData({
                        ...coupon as unknown as CreateCouponRequest,
                        applicableTo: mappedApplicableTo,
                        startDate: formattedStart,
                        endDate: formattedEnd,
                    });
                } else {
                    setBookingScope('internal');
                    const mappedApplicableTo =
                        coupon.eligibleFor === 'package'
                            ? 'wellnessPackage'
                            : coupon.eligibleFor === 'specialist'
                                ? 'consultation'
                                : coupon.applicableTo;

                    setCouponData({
                        ...coupon as unknown as CreateCouponRequest,
                        applicableTo: mappedApplicableTo,
                        startDate: formattedStart,
                        endDate: formattedEnd,
                    });
                    const targetsSpecificInternalItem = coupon.applicableTo === 'specific';
                    setInternalTargetMode(targetsSpecificInternalItem ? 'specific' : 'all');
                    setSelectedApplicableId(targetsSpecificInternalItem ? coupon.applicableIds?.[0] || '' : '');
                }

                const corporateIdFromCoupon = coupon.corporateIds?.[0] || '';
                const couponUserIds = Array.isArray(coupon.userIds) ? coupon.userIds.filter(Boolean) : [];
                if (couponUserIds.length > 0 && corporateIdFromCoupon) {
                    setTargetType('user_and_corporate');
                } else if (couponUserIds.length > 0) {
                    setTargetType('user');
                } else if (corporateIdFromCoupon) {
                    setTargetType('corporate');
                } else {
                    setTargetType('all');
                }
                setSelectedCorporateId(corporateIdFromCoupon);

                if (coupon.imageUrl && typeof coupon.imageUrl === 'string') {
                    setExistingImageUrl(coupon.imageUrl);
                }
            }
        } catch (error) {
            console.error(error);
        }
    }

    React.useEffect(() => {
        if (selectedId && isModalOpen) {
            fetchDetail(selectedId)
        } else if (!selectedId && isModalOpen) {
            setCouponData(defaultData);
        }
    }, [selectedId, isModalOpen]);

    React.useEffect(() => {
        if (!isModalOpen) {
            return;
        }
        fetchAllCorporates();
    }, [isModalOpen, fetchAllCorporates]);

    React.useEffect(() => {
        if (!isModalOpen || !needsUsers) {
            return;
        }

        let cancelled = false;
        setUserOptions([]);
        setUserTotal(0);
        setUserPage(1);
        setHasMoreUsers(false);
        const searchTimer = window.setTimeout(() => {
            setUsersLoading(true);

            const params = new URLSearchParams({ page: '1', limit: '100' });
            const search = userSearchQuery.trim();
            if (selectedCorporateId) {
                params.set('corporateId', selectedCorporateId);
            }
            if (search) {
                params.set('name', search);
            }

            doGET(`/users?${params.toString()}`)
                .then((res) => {
                    if (cancelled) return;

                    const payload = res?.data?.data ?? res?.data ?? {};
                    const users = payload?.users ?? payload?.rows ?? payload?.data ?? payload ?? [];
                    const normalizedUsers = Array.isArray(users) ? users : [];
                    const pagination = res?.data?.pagination ?? payload?.pagination ?? {};
                    const total = Number(pagination.total);
                    setUserOptions(normalizedUsers);
                    setUserTotal(Number.isFinite(total) ? total : normalizedUsers.length);
                    setHasMoreUsers(
                        Number.isFinite(total)
                            ? normalizedUsers.length < total
                            : normalizedUsers.length === 100
                    );
                    setUsersLoading(false);
                }, () => {
                    if (!cancelled) {
                        setUsersLoading(false);
                    }
                });
        }, 300);

        return () => {
            cancelled = true;
            window.clearTimeout(searchTimer);
        };
    }, [isModalOpen, needsUsers, selectedCorporateId, userSearchQuery]);

    const loadMoreUsers = React.useCallback(() => {
        if (usersLoading || !hasMoreUsers) {
            return;
        }

        const nextPage = userPage + 1;
        const params = new URLSearchParams({ page: String(nextPage), limit: '100' });
        const search = userSearchQuery.trim();
        if (selectedCorporateId) {
            params.set('corporateId', selectedCorporateId);
        }
        if (search) {
            params.set('name', search);
        }

        setUsersLoading(true);
        doGET(`/users?${params.toString()}`)
            .then((res) => {
                const payload = res?.data?.data ?? res?.data ?? {};
                const users = payload?.users ?? payload?.rows ?? payload?.data ?? payload ?? [];
                const nextUsers = Array.isArray(users) ? users : [];
                const pagination = res?.data?.pagination ?? payload?.pagination ?? {};
                const total = Number(pagination.total);

                setUserOptions((currentUsers) => {
                    const userIds = new Set(currentUsers.map((user) => user._id));
                    return [...currentUsers, ...nextUsers.filter((user: UserData) => !userIds.has(user._id))];
                });
                setUserPage(nextPage);
                if (Number.isFinite(total)) {
                    setUserTotal(total);
                    setHasMoreUsers(nextPage * 100 < total);
                } else {
                    setHasMoreUsers(nextUsers.length === 100);
                }
                setUsersLoading(false);
            }, () => setUsersLoading(false));
    }, [hasMoreUsers, selectedCorporateId, userPage, userSearchQuery, usersLoading]);

    const handleUserListScroll = (event: React.UIEvent<HTMLElement>) => {
        const listNode = event.currentTarget;
        const isNearBottom = listNode.scrollTop + listNode.clientHeight >= listNode.scrollHeight - 30;
        if (isNearBottom) {
            loadMoreUsers();
        }
    };

    React.useEffect(() => {
        if (!isModalOpen || bookingScope !== 'external' || externalKind !== 'external_specialist') {
            return;
        }
        let cancelled = false;
        (async () => {
            const list = await fetchExternalProviderTypes(extAppointmentType, extPincode);
            if (!cancelled) {
                const rows = list || [];
                setProviderTypes(rows);
                if (rows.length && !selectedProviderType) {
                    setSelectedProviderType(rows[0].providerType);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isModalOpen, bookingScope, externalKind, extAppointmentType, extPincode]);

    React.useEffect(() => {
        if (!isModalOpen || bookingScope !== 'external' || externalKind !== 'external_specialist') {
            return;
        }
        if (extTargetAllSpecialists || !selectedProviderType) {
            setExternalSpecialistsList([]);
            return;
        }
        let cancelled = false;
        (async () => {
            const list = await fetchExternalSpecialists({
                providerType: selectedProviderType,
                appointmentType: extAppointmentType,
                pincode: extPincode,
            });
            if (!cancelled) {
                setExternalSpecialistsList(
                    (list || []).map((s: any) => ({ id: s.id, name: s.name || s.id }))
                );
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [
        isModalOpen,
        bookingScope,
        externalKind,
        extTargetAllSpecialists,
        selectedProviderType,
        extAppointmentType,
        extPincode,
    ]);

    const loadExternalDiagnosticsCatalog = React.useCallback(async () => {
        const pin = extPincode.trim();
        if (pin.length < 6) {
            showError('Enter a valid pincode to load labs and tests');
            return;
        }
        const [labs, tests] = await Promise.all([
            fetchExternalLabs(pin),
            fetchExternalTests({ pincode: pin, labId: selectedLabId || undefined }),
        ]);
        setExternalLabsList((labs || []).map((l: any) => ({ id: l.id, name: l.name || l.id })));
        setExternalTestsList((tests || []).map((t: any) => ({
            id: t.id,
            name: t.name || t.id,
            price: t.price,
            customPrice: t.customPrice,
            image: t.image,
            coverImage: t.coverImage,
        })));
        const latNum = extLat.trim() ? parseFloat(extLat) : NaN;
        const lngNum = extLng.trim() ? parseFloat(extLng) : NaN;
        if (!Number.isNaN(latNum) && !Number.isNaN(lngNum)) {
            const pkgs = await fetchExternalPackages(latNum, lngNum);
            setExternalPackagesList((pkgs || []).map((p: any) => ({
                id: p.id,
                name: p.name || p.id,
                price: p.price,
                customPrice: p.customPrice,
                image: p.image,
                coverImage: p.coverImage,
            })));
        } else {
            const pkgs = await fetchExternalPackages();
            setExternalPackagesList((pkgs || []).map((p: any) => ({
                id: p.id,
                name: p.name || p.id,
                price: p.price,
                customPrice: p.customPrice,
                image: p.image,
                coverImage: p.coverImage,
            })));
        }
    }, [extPincode, extLat, extLng, selectedLabId]);

    React.useEffect(() => {
        if (!isModalOpen) {
            return;
        }
        let cancelled = false;
        const loadOptions = async () => {
            if (bookingScope !== 'internal') {
                return;
            }
            if (
                internalTargetMode !== 'specific' ||
                (couponData.applicableTo !== 'wellnessPackage' && couponData.applicableTo !== 'consultation')
            ) {
                setApplicableOptions([]);
                setSelectedApplicableId("");
                return;
            }
            const corporateIdForFilter =
                needsCorporate ? selectedCorporateId || globalCompanyId : "";
            if (needsCorporate && !corporateIdForFilter) {
                setApplicableOptions([]);
                setSelectedApplicableId("");
                return;
            }
            if (couponData.applicableTo === 'wellnessPackage') {
                const list = corporateIdForFilter
                    ? await fetchPackagesByCorporate(corporateIdForFilter)
                    : ((await doGET('/wellness-packages?page=1&limit=100'))?.data?.data || []);
                if (cancelled) return;
                const mapped = (list || []).map((item: any) => ({
                    _id: item._id,
                    label: item.name || item._id,
                }));
                setApplicableOptions(mapped);
            } else {
                const list = corporateIdForFilter
                    ? await fetchSpecialistsByCorporate(corporateIdForFilter)
                    : ((await doGET('/specialists?page=1&limit=100'))?.data?.data || []);
                if (cancelled) return;
                const mapped = (list || []).map((item: any) => ({
                    _id: item._id,
                    label: item.name || item._id,
                }));
                setApplicableOptions(mapped);
            }
        };
        loadOptions();
        return () => {
            cancelled = true;
        };
    }, [
        isModalOpen,
        couponData.applicableTo,
        internalTargetMode,
        targetType,
        needsCorporate,
        selectedCorporateId,
        globalCompanyId,
        fetchPackagesByCorporate,
        fetchSpecialistsByCorporate,
        fetchPackagesGrid,
        fetchSpecialistsGrid,
        bookingScope,
    ]);

    return (
        <div>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                >
                    <SearchInput handleChange={handleSearchChange} />
                    {!hideAddButton && (
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleClickOpen}
                        >
                            Add Coupon
                        </Button>
                    )}
                </Stack>

                <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Filter by Applicable To</InputLabel>
                        <Select
                            value={(filters as any).applicableTo || ''}
                            label="Filter by Applicable To"
                            onChange={(e) => setFilters({ ...filters, applicableTo: e.target.value || undefined })}
                        >
                            <MenuItem value="">All Applicable</MenuItem>
                            {APPLICABLE_TO_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Filter by Validity</InputLabel>
                        <Select
                            value={
                                (filters as any).isActive === true ? 'active' :
                                (filters as any).isActive === false ? 'inactive' : ''
                            }
                            label="Filter by Validity"
                            onChange={(e) => {
                                const val = e.target.value;
                                setFilters({ 
                                    ...filters, 
                                    isActive: val === 'active' ? true : (val === 'inactive' ? false : undefined) 
                                });
                            }}
                        >
                            <MenuItem value="">All Statuses</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Filter by Discount Type</InputLabel>
                        <Select
                            value={(filters as any).discountType || ''}
                            label="Filter by Discount Type"
                            onChange={(e) => setFilters({ ...filters, discountType: e.target.value || undefined })}
                        >
                            <MenuItem value="">All Discounts</MenuItem>
                            {DISCOUNT_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </Box>

            <Drawer
                anchor="right"
                open={isModalOpen}
                onClose={handleClose}
            >
                <Box sx={{ width: { xs: '100%', sm: 600 }, p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white' }}>
                        <Typography variant="h6">{selectedId ? 'Edit Coupon' : 'Add Coupon'}</Typography>
                        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={2}>

                                {/* Basic Info */}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>Basic Information</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        margin="dense"
                                        id="code"
                                        label="Coupon Code"
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        placeholder="e.g. WELCOME10"
                                        inputProps={{ style: { textTransform: 'uppercase' } }}
                                        value={couponData.code}
                                        required
                                        onChange={(e) => handleChange("code", e.target.value.toUpperCase())}
                                        error={!!fieldErrors.code}
                                        helperText={fieldErrors.code}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        margin="dense"
                                        id="title"
                                        label="Title"
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        placeholder="Display Title"
                                        value={couponData.title}
                                        onChange={(e) => handleChange("title", e.target.value)}
                                        error={!!fieldErrors.title}
                                        helperText={fieldErrors.title}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        margin="dense"
                                        id="description"
                                        label="Description"
                                        fullWidth
                                        multiline
                                        rows={2}
                                        size="small"
                                        variant="outlined"
                                        placeholder="Coupon Description"
                                        value={couponData.description}
                                        onChange={(e) => handleChange("description", e.target.value)}
                                    />
                                </Grid>

                                {/* Discount Details */}
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 1, fontWeight: 'bold' }}>Discount Details</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth margin="dense" size="small">
                                        <InputLabel id="discount-type-label">Discount Type</InputLabel>
                                        <Select
                                            labelId="discount-type-label"
                                            id="discountType"
                                            value={couponData.discountType}
                                            label="Discount Type"
                                            onChange={(e) => handleChange("discountType", e.target.value)}
                                        >
                                            {DISCOUNT_TYPES.map((type) => (
                                                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        margin="dense"
                                        id="discountValue"
                                        label={couponData.discountType === 'percentage' ? "Discount (%)" : "Discount Amount"}
                                        type="number"
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        value={couponData.discountValue ?? ''}
                                        required
                                        onChange={(e) => handleChange("discountValue", e.target.value)}
                                        error={!!fieldErrors.discountValue}
                                        helperText={fieldErrors.discountValue}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        margin="dense"
                                        id="usageLimitPerUser"
                                        label="Limit Per User"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        inputProps={{ min: 1 }}
                                        value={couponData.usageLimitPerUser ?? ''}
                                        onChange={(e) => handleChange("usageLimitPerUser", e.target.value)}
                                        error={!!fieldErrors.usageLimitPerUser}
                                        helperText={fieldErrors.usageLimitPerUser || 'Maximum redemptions allowed for each user'}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        margin="dense"
                                        id="maximumDiscountAmount"
                                        label="Max Discount Amount"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        value={couponData.maximumDiscountAmount ?? ''}
                                        onChange={(e) => handleChange("maximumDiscountAmount", e.target.value)}
                                        disabled={couponData.discountType === 'fixed'}
                                        error={!!fieldErrors.maximumDiscountAmount}
                                        helperText={fieldErrors.maximumDiscountAmount || '0 means no cap. For percentage discounts only.'}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        margin="dense"
                                        id="minimumPurchaseAmount"
                                        label="Min Purchase Amount"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        value={couponData.minimumPurchaseAmount ?? ''}
                                        onChange={(e) => handleChange("minimumPurchaseAmount", e.target.value)}
                                        error={!!fieldErrors.minimumPurchaseAmount}
                                        helperText={fieldErrors.minimumPurchaseAmount}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth margin="dense" size="small">
                                        <InputLabel id="coupon-for-user-label">Booking User</InputLabel>
                                        <Select
                                            labelId="coupon-for-user-label"
                                            id="forUser"
                                            value={couponData.forUser || 'all'}
                                            label="Booking User"
                                            onChange={(e) => handleChange("forUser", e.target.value)}
                                        >
                                            <MenuItem value="self">Self only</MenuItem>
                                            <MenuItem value="all">Self + Dependents</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Applicability */}
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 1, fontWeight: 'bold' }}>Validity & Limits</Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            row
                                            value={targetType}
                                            onChange={(e) => {
                                                const nextTarget = e.target.value as CouponAudience;
                                                setTargetType(nextTarget);
                                                if (nextTarget === 'all') {
                                                    setSelectedCorporateId("");
                                                    handleChange("userIds", []);
                                                } else if (nextTarget === 'corporate') {
                                                    handleChange("userIds", []);
                                                    if (!selectedCorporateId && globalCompanyId && globalCompanyId !== 'general') {
                                                        setSelectedCorporateId(globalCompanyId);
                                                    }
                                                } else if (nextTarget === 'user') {
                                                    setSelectedCorporateId("");
                                                } else {
                                                    handleChange("userIds", []);
                                                    if (!selectedCorporateId && globalCompanyId && globalCompanyId !== 'general') {
                                                        setSelectedCorporateId(globalCompanyId);
                                                    }
                                                }
                                                if (fieldErrors.corporateIds || fieldErrors.userIds) {
                                                    setFieldErrors((prev) => ({ ...prev, corporateIds: '', userIds: '' }));
                                                }
                                            }}
                                        >
                                            <FormControlLabel value="all" control={<Radio />} label="For All" />
                                            <FormControlLabel value="corporate" control={<Radio />} label="For Corporate" />
                                            <FormControlLabel value="user" control={<Radio />} label="For User" />
                                            <FormControlLabel
                                                value="user_and_corporate"
                                                control={<Radio />}
                                                label="For User + Corporate"
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        User + Corporate is an AND rule: the selected user must book under the selected corporate.
                                    </Typography>
                                </Grid>

                                {needsCorporate && (
                                    <Grid item xs={12}>
                                        <FormControl fullWidth margin="dense" size="small" error={!!fieldErrors.corporateIds}>
                                            <InputLabel id="coupon-corporate-select-label">Select Corporate</InputLabel>
                                            <Select
                                                labelId="coupon-corporate-select-label"
                                                label="Select Corporate"
                                                value={selectedCorporateId}
                                                onChange={(e) => {
                                                    setSelectedCorporateId(e.target.value);
                                                    if (targetType === 'user_and_corporate') {
                                                        handleChange("userIds", []);
                                                    }
                                                    if (fieldErrors.corporateIds) {
                                                        setFieldErrors((prev) => ({ ...prev, corporateIds: '' }));
                                                    }
                                                }}
                                            >
                                                {corporates.map((corporate: any) => (
                                                    <MenuItem key={corporate._id} value={corporate._id}>
                                                        {corporate.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {fieldErrors.corporateIds ? (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                                    {fieldErrors.corporateIds}
                                                </Typography>
                                            ) : null}
                                        </FormControl>
                                    </Grid>
                                )}

                                {needsUsers && (
                                    <Grid item xs={12}>
                                        <FormControl
                                            fullWidth
                                            margin="dense"
                                            size="small"
                                            error={!!fieldErrors.userIds}
                                            disabled={(usersLoading && userOptions.length === 0) || (targetType === 'user_and_corporate' && !selectedCorporateId)}
                                        >
                                            <InputLabel id="coupon-user-multiselect-label">
                                                {usersLoading ? 'Loading Users...' : 'Select Users'}
                                            </InputLabel>
                                            <Select
                                                labelId="coupon-user-multiselect-label"
                                                multiple
                                                label={usersLoading ? 'Loading Users...' : 'Select Users'}
                                                value={selectedUserIds}
                                                onClose={() => setUserSearchQuery('')}
                                                MenuProps={{
                                                    autoFocus: false,
                                                    PaperProps: {
                                                        sx: { maxHeight: 360 },
                                                        onScroll: handleUserListScroll,
                                                    },
                                                }}
                                                onChange={(e) => {
                                                    const selectedIds = typeof e.target.value === 'string'
                                                        ? e.target.value.split(',')
                                                        : e.target.value as string[];
                                                    handleChange("userIds", selectedIds);
                                                    if (fieldErrors.userIds) {
                                                        setFieldErrors((prev) => ({ ...prev, userIds: '' }));
                                                    }
                                                }}
                                                renderValue={(selected) => {
                                                    const count = (selected as string[]).length;
                                                    return `${count} ${count === 1 ? 'user' : 'users'} selected`;
                                                }}
                                            >
                                                <ListSubheader sx={{ py: 1, bgcolor: 'background.paper' }}>
                                                    <TextField
                                                        autoFocus
                                                        fullWidth
                                                        size="small"
                                                        placeholder="Search by name, email, or phone"
                                                        value={userSearchQuery}
                                                        onChange={(e) => setUserSearchQuery(e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onKeyDown={(e) => {
                                                            if (e.key !== 'Escape') {
                                                                e.stopPropagation();
                                                            }
                                                        }}
                                                    />
                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                        {normalizedUserSearch
                                                            ? `${userTotal} matching ${userTotal === 1 ? 'user' : 'users'} (loaded ${filteredSelectableUserOptions.length})`
                                                            : `${userTotal} ${userTotal === 1 ? 'user' : 'users'} available (loaded ${filteredSelectableUserOptions.length})`}
                                                    </Typography>
                                                </ListSubheader>
                                                {filteredSelectableUserOptions.length === 0 ? (
                                                    <MenuItem disabled>
                                                        {normalizedUserSearch ? 'No users match your search' : 'No users available'}
                                                    </MenuItem>
                                                ) : filteredSelectableUserOptions.map((user) => (
                                                    <MenuItem key={user._id || user.email || user.phone} value={user._id}>
                                                        <Checkbox checked={selectedUserIds.includes(user._id)} />
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                {user.name || 'Unnamed User'}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {[user.email, user.phone].filter(Boolean).join(' | ') || user._id}
                                                            </Typography>
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <Typography
                                                variant="caption"
                                                color={fieldErrors.userIds ? 'error' : 'text.secondary'}
                                                sx={{ mt: 0.5, mx: 1.75 }}
                                            >
                                                {fieldErrors.userIds || (
                                                    targetType === 'user_and_corporate'
                                                        ? 'Select a corporate first; only its users can be selected'
                                                        : `${selectedUserIds.length} ${selectedUserIds.length === 1 ? 'user' : 'users'} selected`
                                                )}
                                            </Typography>
                                        </FormControl>
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                        Booking category
                                    </Typography>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            row
                                            value={bookingScope}
                                            onChange={(e) => {
                                                const v = e.target.value as 'all' | 'internal' | 'external';
                                                setBookingScope(v);
                                                const hasInternalCategory =
                                                    couponData.applicableTo === 'all' ||
                                                    couponData.applicableTo === 'wellnessPackage' ||
                                                    couponData.applicableTo === 'consultation';
                                                if (v === 'internal' && (!canSelectAllInternal || !hasInternalCategory)) {
                                                    handleChange(
                                                        'applicableTo',
                                                        couponData.eligibleFor === 'specialist' ||
                                                            couponData.eligibleFor === 'external_specialist'
                                                            ? 'consultation'
                                                            : 'wellnessPackage'
                                                    );
                                                    setInternalTargetMode('all');
                                                }
                                                if (fieldErrors.externalDiagnostics) {
                                                    setFieldErrors((prev) => ({ ...prev, externalDiagnostics: '' }));
                                                }
                                                if (fieldErrors.externalProvider) {
                                                    setFieldErrors((prev) => ({ ...prev, externalProvider: '', externalSpecialist: '' }));
                                                }
                                                if (fieldErrors.applicableIds) {
                                                    setFieldErrors((prev) => ({ ...prev, applicableIds: '' }));
                                                }
                                            }}
                                        >
                                            <FormControlLabel value="all" control={<Radio />} label="All categories" />
                                            <FormControlLabel value="internal" control={<Radio />} label="Internal (EWA)" />
                                            <FormControlLabel value="external" control={<Radio />} label="External (Labstack)" />
                                        </RadioGroup>
                                    </FormControl>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        The backend supports one category at a time, or All categories. Mixed subsets such as package + external diagnostics are not supported.
                                    </Typography>
                                    {appliesToWalletRecharge && (
                                        <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                                            Backend note: category-wide coupons with Applicable To = All also work on wallet recharge; wallet validation does not enforce User, Corporate, or Self-only targeting.
                                        </Typography>
                                    )}
                                </Grid>

                                {bookingScope === 'external' && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth margin="dense" size="small">
                                                <InputLabel id="external-kind-label">External type</InputLabel>
                                                <Select
                                                    labelId="external-kind-label"
                                                    label="External type"
                                                    value={externalKind}
                                                    onChange={(e) =>
                                                        setExternalKind(e.target.value as 'external_specialist' | 'external_diagnostics')
                                                    }
                                                >
                                                    <MenuItem value="external_specialist">External specialist</MenuItem>
                                                    <MenuItem value="external_diagnostics">External diagnostics (packages / tests)</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {externalKind === 'external_specialist' && (
                                            <>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        margin="dense"
                                                        label="Appointment type"
                                                        size="small"
                                                        fullWidth
                                                        value={extAppointmentType}
                                                        onChange={(e) => setExtAppointmentType(e.target.value)}
                                                        helperText="e.g. ONLINE"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        margin="dense"
                                                        label="Pincode (optional)"
                                                        size="small"
                                                        fullWidth
                                                        value={extPincode}
                                                        onChange={(e) => setExtPincode(e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={extTargetAllSpecialists}
                                                                onChange={(e) => {
                                                                    setExtTargetAllSpecialists(e.target.checked);
                                                                    if (e.target.checked) {
                                                                        setSelectedExternalSpecialistId('');
                                                                    }
                                                                }}
                                                            />
                                                        }
                                                        label="All external specialists (any Labstack provider)"
                                                    />
                                                </Grid>
                                                {!extTargetAllSpecialists && (
                                                    <>
                                                        <Grid item xs={12} sm={6}>
                                                            <FormControl
                                                                fullWidth
                                                                margin="dense"
                                                                size="small"
                                                                error={!!fieldErrors.externalProvider}
                                                            >
                                                                <InputLabel>Provider type</InputLabel>
                                                                <Select
                                                                    label="Provider type"
                                                                    value={selectedProviderType}
                                                                    onChange={(e) => {
                                                                        setSelectedProviderType(e.target.value);
                                                                        setFieldErrors((prev) => ({
                                                                            ...prev,
                                                                            externalProvider: '',
                                                                        }));
                                                                    }}
                                                                >
                                                                    {providerTypes.map((pt) => (
                                                                        <MenuItem
                                                                            key={pt.providerType}
                                                                            value={pt.providerType}
                                                                        >
                                                                            {pt.name || pt.providerType}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                                {fieldErrors.externalProvider ? (
                                                                    <Typography variant="caption" color="error">
                                                                        {fieldErrors.externalProvider}
                                                                    </Typography>
                                                                ) : null}
                                                            </FormControl>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <FormControl
                                                                fullWidth
                                                                margin="dense"
                                                                size="small"
                                                                error={!!fieldErrors.externalSpecialist}
                                                            >
                                                                <InputLabel>External specialist</InputLabel>
                                                                <Select
                                                                    label="External specialist"
                                                                    value={selectedExternalSpecialistId}
                                                                    onChange={(e) => {
                                                                        setSelectedExternalSpecialistId(e.target.value);
                                                                        setFieldErrors((prev) => ({
                                                                            ...prev,
                                                                            externalSpecialist: '',
                                                                        }));
                                                                    }}
                                                                >
                                                                    {externalSpecialistsList.map((s) => (
                                                                        <MenuItem key={s.id} value={s.id}>
                                                                            {s.name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                                {fieldErrors.externalSpecialist ? (
                                                                    <Typography variant="caption" color="error">
                                                                        {fieldErrors.externalSpecialist}
                                                                    </Typography>
                                                                ) : null}
                                                            </FormControl>
                                                        </Grid>
                                                    </>
                                                )}
                                            </>
                                        )}

                                        {externalKind === 'external_diagnostics' && (
                                            <>
                                                <Grid item xs={12}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={extTargetAllDiagnostics}
                                                                onChange={(e) => {
                                                                    const targetsAll = e.target.checked;
                                                                    setExtTargetAllDiagnostics(targetsAll);
                                                                    if (targetsAll) {
                                                                        setSelectedLabId('');
                                                                        setSelectedPackageIds([]);
                                                                        setSelectedTestIds([]);
                                                                        setExtDiagBothRequired(false);
                                                                        setFieldErrors((prev) => ({ ...prev, externalDiagnostics: '' }));
                                                                    }
                                                                }}
                                                            />
                                                        }
                                                        label="All external diagnostics (any lab, package, or test)"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <TextField
                                                        margin="dense"
                                                        label="Pincode"
                                                        size="small"
                                                        fullWidth
                                                        value={extPincode}
                                                        onChange={(e) => setExtPincode(e.target.value)}
                                                        helperText="Used to load labs & tests"
                                                        disabled={extTargetAllDiagnostics}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <TextField
                                                        margin="dense"
                                                        label="Latitude (optional)"
                                                        size="small"
                                                        fullWidth
                                                        value={extLat}
                                                        onChange={(e) => setExtLat(e.target.value)}
                                                        disabled={extTargetAllDiagnostics}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <TextField
                                                        margin="dense"
                                                        label="Longitude (optional)"
                                                        size="small"
                                                        fullWidth
                                                        value={extLng}
                                                        onChange={(e) => setExtLng(e.target.value)}
                                                        disabled={extTargetAllDiagnostics}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={loadExternalDiagnosticsCatalog}
                                                        disabled={extTargetAllDiagnostics}
                                                    >
                                                        Load labs, packages & tests
                                                    </Button>
                                                    {fieldErrors.externalDiagnostics ? (
                                                        <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                                                            {fieldErrors.externalDiagnostics}
                                                        </Typography>
                                                    ) : null}
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FormControl fullWidth margin="dense" size="small">
                                                        <InputLabel>Lab (optional)</InputLabel>
                                                        <Select
                                                            label="Lab (optional)"
                                                            value={selectedLabId}
                                                            onChange={(e) => setSelectedLabId(e.target.value)}
                                                            disabled={extTargetAllDiagnostics}
                                                        >
                                                            <MenuItem value="">
                                                                <em>Any / not specified</em>
                                                            </MenuItem>
                                                            {externalLabsList.map((lab) => (
                                                                <MenuItem key={lab.id} value={lab.id}>
                                                                    {lab.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={extDiagBothRequired && canRequireDiagnosticBoth}
                                                                onChange={(e) => setExtDiagBothRequired(e.target.checked)}
                                                                disabled={!canRequireDiagnosticBoth}
                                                            />
                                                        }
                                                        label="Require both lab and package/test match when both are set"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FormControl fullWidth margin="dense" size="small">
                                                        <InputLabel>Packages</InputLabel>
                                                        <Select
                                                            multiple
                                                            label="Packages"
                                                            value={selectedPackageIds}
                                                            disabled={extTargetAllDiagnostics}
                                                            onClose={() => {
                                                                setPackageSearchInput('');
                                                                setPackageSearchQuery('');
                                                            }}
                                                            MenuProps={{
                                                                autoFocus: false,
                                                                PaperProps: { sx: { maxHeight: 360 } },
                                                            }}
                                                            onChange={(e) =>
                                                                setSelectedPackageIds(
                                                                    typeof e.target.value === 'string'
                                                                        ? e.target.value.split(',')
                                                                        : (e.target.value as string[])
                                                                )
                                                            }
                                                            renderValue={(selected) => {
                                                                const count = (selected as string[]).length;
                                                                return `${count} ${count === 1 ? 'package' : 'packages'} selected`;
                                                            }}
                                                        >
                                                            <ListSubheader sx={{ py: 1, bgcolor: 'background.paper' }}>
                                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                                    <TextField
                                                                        autoFocus
                                                                        fullWidth
                                                                        size="small"
                                                                        placeholder="Search packages"
                                                                        value={packageSearchInput}
                                                                        onChange={(e) => setPackageSearchInput(e.target.value)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter') {
                                                                                e.preventDefault();
                                                                                setPackageSearchQuery(packageSearchInput);
                                                                            }
                                                                            if (e.key !== 'Escape') {
                                                                                e.stopPropagation();
                                                                            }
                                                                        }}
                                                                    />
                                                                    <Button
                                                                        variant="contained"
                                                                        size="small"
                                                                        onMouseDown={(e) => e.preventDefault()}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setPackageSearchQuery(packageSearchInput);
                                                                        }}
                                                                    >
                                                                        Search
                                                                    </Button>
                                                                </Box>
                                                            </ListSubheader>
                                                            {filteredExternalPackages.length === 0 ? (
                                                                <MenuItem disabled>
                                                                    {normalizedPackageSearch ? 'No packages match your search' : 'No packages available'}
                                                                </MenuItem>
                                                            ) : filteredExternalPackages.map((p) => (
                                                                <MenuItem key={p.id} value={p.id}>
                                                                    <Checkbox checked={selectedPackageIds.includes(p.id)} />
                                                                    <Box sx={{ minWidth: 0 }}>
                                                                        <Typography variant="body2">{p.name}</Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {formatExternalCatalogPrice(p) || p.id}
                                                                        </Typography>
                                                                    </Box>
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FormControl fullWidth margin="dense" size="small">
                                                        <InputLabel>Tests</InputLabel>
                                                        <Select
                                                            multiple
                                                            label="Tests"
                                                            value={selectedTestIds}
                                                            disabled={extTargetAllDiagnostics}
                                                            onClose={() => {
                                                                setTestSearchInput('');
                                                                setTestSearchQuery('');
                                                            }}
                                                            MenuProps={{
                                                                autoFocus: false,
                                                                PaperProps: { sx: { maxHeight: 360 } },
                                                            }}
                                                            onChange={(e) =>
                                                                setSelectedTestIds(
                                                                    typeof e.target.value === 'string'
                                                                        ? e.target.value.split(',')
                                                                        : (e.target.value as string[])
                                                                )
                                                            }
                                                            renderValue={(selected) => {
                                                                const count = (selected as string[]).length;
                                                                return `${count} ${count === 1 ? 'test' : 'tests'} selected`;
                                                            }}
                                                        >
                                                            <ListSubheader sx={{ py: 1, bgcolor: 'background.paper' }}>
                                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                                    <TextField
                                                                        autoFocus
                                                                        fullWidth
                                                                        size="small"
                                                                        placeholder="Search tests"
                                                                        value={testSearchInput}
                                                                        onChange={(e) => setTestSearchInput(e.target.value)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter') {
                                                                                e.preventDefault();
                                                                                setTestSearchQuery(testSearchInput);
                                                                            }
                                                                            if (e.key !== 'Escape') {
                                                                                e.stopPropagation();
                                                                            }
                                                                        }}
                                                                    />
                                                                    <Button
                                                                        variant="contained"
                                                                        size="small"
                                                                        onMouseDown={(e) => e.preventDefault()}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setTestSearchQuery(testSearchInput);
                                                                        }}
                                                                    >
                                                                        Search
                                                                    </Button>
                                                                </Box>
                                                            </ListSubheader>
                                                            {filteredExternalTests.length === 0 ? (
                                                                <MenuItem disabled>
                                                                    {normalizedTestSearch ? 'No tests match your search' : 'No tests available'}
                                                                </MenuItem>
                                                            ) : filteredExternalTests.map((t) => (
                                                                <MenuItem key={t.id} value={t.id}>
                                                                    <Checkbox checked={selectedTestIds.includes(t.id)} />
                                                                    <Box sx={{ minWidth: 0 }}>
                                                                        <Typography variant="body2">{t.name}</Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {formatExternalCatalogPrice(t) || t.id}
                                                                        </Typography>
                                                                    </Box>
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            </>
                                        )}
                                    </>
                                )}

                                {bookingScope === 'internal' && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth margin="dense" size="small" error={!!fieldErrors.applicableTo}>
                                                <InputLabel id="applicable-to-label">Internal category</InputLabel>
                                                <Select
                                                    labelId="applicable-to-label"
                                                    id="applicableTo"
                                                    value={couponData.applicableTo}
                                                    label="Internal category"
                                                    onChange={(e) => {
                                                        handleChange("applicableTo", e.target.value);
                                                        setInternalTargetMode('all');
                                                        setSelectedApplicableId('');
                                                    }}
                                                >
                                                    <MenuItem value="all" disabled={!canSelectAllInternal}>All internal bookings</MenuItem>
                                                    <MenuItem value="wellnessPackage">Wellness packages only</MenuItem>
                                                    <MenuItem value="consultation">Specialists only</MenuItem>
                                                </Select>
                                                {fieldErrors.applicableTo ? (
                                                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                                        {fieldErrors.applicableTo}
                                                    </Typography>
                                                ) : !canSelectAllInternal ? (
                                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                                        All internal is unavailable for this edit because the backend update API cannot unset its existing booking category.
                                                    </Typography>
                                                ) : null}
                                            </FormControl>
                                        </Grid>

                                        {(couponData.applicableTo === 'wellnessPackage' || couponData.applicableTo === 'consultation') && (
                                            <>
                                                <Grid item xs={12} sm={6}>
                                                    <FormControl fullWidth margin="dense" size="small">
                                                        <InputLabel id="internal-target-mode-label">Coverage</InputLabel>
                                                        <Select
                                                            labelId="internal-target-mode-label"
                                                            label="Coverage"
                                                            value={internalTargetMode}
                                                            onChange={(e) => {
                                                                const mode = e.target.value as InternalTargetMode;
                                                                setInternalTargetMode(mode);
                                                                if (mode === 'all') {
                                                                    setSelectedApplicableId('');
                                                                    setFieldErrors((prev) => ({ ...prev, applicableIds: '' }));
                                                                }
                                                            }}
                                                        >
                                                            <MenuItem value="all">
                                                                {couponData.applicableTo === 'wellnessPackage'
                                                                    ? 'All wellness packages'
                                                                    : 'All specialists'}
                                                            </MenuItem>
                                                            <MenuItem value="specific">
                                                                {couponData.applicableTo === 'wellnessPackage'
                                                                    ? 'One specific wellness package'
                                                                    : 'One specific specialist'}
                                                            </MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                {internalTargetMode === 'specific' && (
                                                    <Grid item xs={12} sm={6}>
                                                        <FormControl fullWidth margin="dense" size="small" error={!!fieldErrors.applicableIds}>
                                                            <InputLabel id="applicable-item-label">
                                                                {couponData.applicableTo === 'wellnessPackage' ? 'Select Wellness Package' : 'Select Specialist'}
                                                            </InputLabel>
                                                            <Select
                                                                labelId="applicable-item-label"
                                                                label={couponData.applicableTo === 'wellnessPackage' ? 'Select Wellness Package' : 'Select Specialist'}
                                                                value={selectedApplicableId}
                                                                onChange={(e) => {
                                                                    setSelectedApplicableId(e.target.value);
                                                                    if (fieldErrors.applicableIds) {
                                                                        setFieldErrors((prev) => ({ ...prev, applicableIds: '' }));
                                                                    }
                                                                }}
                                                            >
                                                                {applicableOptions.map((option) => (
                                                                    <MenuItem key={option._id} value={option._id}>
                                                                        {option.label}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                            {fieldErrors.applicableIds ? (
                                                                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                                                    {fieldErrors.applicableIds}
                                                                </Typography>
                                                            ) : null}
                                                        </FormControl>
                                                    </Grid>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}

                                {/* Validity Dates */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        margin="dense"
                                        id="startDate"
                                        label="Start Date"
                                        type="date"
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        value={couponData.startDate}
                                        required
                                        onChange={(e) => handleChange("startDate", e.target.value)}
                                        error={!!fieldErrors.startDate}
                                        helperText={fieldErrors.startDate}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        margin="dense"
                                        id="endDate"
                                        label="End Date"
                                        type="date"
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        value={couponData.endDate}
                                        required
                                        onChange={(e) => handleChange("endDate", e.target.value)}
                                        error={!!fieldErrors.endDate}
                                        helperText={fieldErrors.endDate}
                                    />
                                </Grid>

                                {/* Usage Limits */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        margin="dense"
                                        id="totalUsageLimit"
                                        label="Total Usage Limit"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        value={couponData.totalUsageLimit ?? ''}
                                        onChange={(e) => handleChange("totalUsageLimit", e.target.value)}
                                        error={!!fieldErrors.totalUsageLimit}
                                        helperText={fieldErrors.totalUsageLimit}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={couponData.isActive || false}
                                                onChange={(e) => handleChange("isActive", e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Is Active"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 1, fontWeight: 'bold' }}>Coupon Image</Typography>
                                    {existingImageUrl ? <CustomImage src={existingImageUrl} style={{ width: '50%', height: 200, objectFit: 'contain', marginBottom: 8 }} /> : null}
                                    <ImageUpload
                                        onChange={(files: any) => {
                                            imageFileRef.current = files?.length ? files[0] : null;
                                            if (files?.length) {
                                                setExistingImageUrl("");
                                            }
                                        }}
                                        allow="image/*"
                                    />
                                </Grid>

                            </Grid>

                        </form>
                    </Box>

                    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleClose} variant="outlined" color="error">Cancel</Button>
                        <Button onClick={handleSubmit(onSubmit)} variant="contained" color="primary">
                            Submit
                        </Button>
                    </Box>
                </Box>
            </Drawer>
        </div>
    );
}
